import React, { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import PublishStatusComponent from '../../components/PublishStatusComponent';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { BaseCommandManagement } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { Command } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Commands/PathItems';
import AISuggestions from '../../components/AISuggestions';
import { Published } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems';


interface EditCommandProps {
  id: string;
  onBack: () => void;
}


function EditCommand({ id, onBack }: EditCommandProps) {
  const { onError } = useToast();
  const [command, setCommand] = useState<Command | null>(null);
  const [name, setName] = useState('');
  const [translationKey, setTranslationKey] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionTranslationKey, setDescriptionTranslationKey] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [state, setState] = useState<Published>({ state: 'draft', isDirty: false });

  const loadCommand = async () => {
    onError((await Session.getCommandClient(id).request(BaseCommandManagement.getCommand()))
      .maybeOk((cmd: Command) => {
        if (cmd) {
          setCommand(cmd);
          setName(cmd.name);
          setTranslationKey(cmd.translationKey);
          setDescription(cmd.description);
          setDescriptionTranslationKey(cmd.descriptionTranslationKey);
          setState(cmd.state);
        }
      }));
  };

  useEffect(() => {
    loadCommand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function patchField<T>(
    operation: any,
    newValue: T,
    currentValue: T,
    revertHandler: (value: T) => void
  ): Promise<void> {
    if (newValue === currentValue) return;
    const client = Session.getClient().addPathParam('commandId', id);
    onError((await client.request(operation)).maybeOk(loadCommand));
  }

  if (!command) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <AISuggestions 
            client={Session.getCommandClient(id)}
            currentSnapshot={JSON.stringify(command)}
            usageInstructions={BaseCommandManagement.toAgentInstructions()}
            developerMessage='The user is in the context of a single command that he/she is editing'
            onOperationsApplied={() => loadCommand()}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Command Details
        </Typography>
        <PublishStatusComponent
          status={command.state}
          onTransition={async (action) => {
            return (await Session.getCommandClient(command.id).request(BaseCommandManagement.updateCommandState({ stateTransition: action })))
              .maybeOk(() => loadCommand());
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 350, maxWidth: 500 }}>
        <TextField
          label="Command Name"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={() => patchField(BaseCommandManagement.updateCommandName({ name }), name, command.name, setName)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Translation Key"
          value={translationKey}
          disabled
          sx={{ mb: 2 }}
        />
        <TextField
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          onBlur={() => patchField(BaseCommandManagement.updateCommandDescription({ description }), description, command.description, setDescription)}
          sx={{ mb: 2 }}
          multiline
          minRows={2}
        />
        <TextField
          label="Description Translation Key"
          value={descriptionTranslationKey}
          disabled
          sx={{ mb: 2 }}
        />
      </Box>
    </Box>
  );
}

export default EditCommand;
